import { useRef, useEffect, useState, useLayoutEffect } from "react";
import type { AgentStatus, EventEntry } from "@/api/types";
import { timeAgo } from "@/api/types";
import { OutputFileChip } from "./file-icon";

interface SessionPreviewProps {
  events: EventEntry[];
  agentStatus: AgentStatus;
  lastActivityAt: string | null;
  showBorder?: boolean;
  expanded?: boolean;
  inverted?: boolean;
  onClick?: () => void;
}

function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/^[-*]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\|[^|]*\|/g, " ")
    .replace(/---+/g, " ")
    .replace(/\n{2,}/g, " ")
    .replace(/\n/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function extractOutputFiles(events: EventEntry[]): { name: string }[] {
  const files: { name: string }[] = [];
  for (const e of events) {
    if (e.type !== "assistant") continue;
    const msg = e.message as Record<string, unknown> | undefined;
    const content = msg?.content;
    if (!Array.isArray(content)) continue;
    for (const block of content) {
      if (typeof block !== "object" || block === null) continue;
      const b = block as Record<string, unknown>;
      if (
        b.type === "tool_use" &&
        (b.name === "Write" || b.name === "write_file")
      ) {
        const input = b.input as Record<string, unknown> | undefined;
        const path = (input?.file_path ?? input?.path) as string | undefined;
        if (path) {
          const name = path.split("/").pop() ?? path;
          if (!files.some((f) => f.name === name)) files.push({ name });
        }
      }
    }
  }
  return files.slice(0, 3);
}

interface MessagePair {
  user: string | null;
  agent: string | null;
}

function extractMessagePairs(events: EventEntry[]): MessagePair[] {
  const pairs: MessagePair[] = [];
  let currentUser: string | null = null;

  for (const e of events) {
    if (e.type === "user_prompt") {
      currentUser = stripMarkdown(e.prompt as string);
    } else if (e.type === "assistant") {
      const msg = e.message as Record<string, unknown> | undefined;
      const content = msg?.content;
      if (!Array.isArray(content)) continue;
      for (const block of content) {
        if (typeof block !== "object" || block === null) continue;
        const b = block as Record<string, unknown>;
        if (b.type === "text" && b.text) {
          pairs.push({
            user: currentUser,
            agent: stripMarkdown(b.text as string),
          });
          currentUser = null;
          break;
        }
      }
    }
  }
  if (currentUser) {
    pairs.push({ user: currentUser, agent: null });
  }
  return pairs;
}

const textBase: React.CSSProperties = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
};

export function SessionPreview({
  events,
  agentStatus,
  lastActivityAt,
  showBorder,
  expanded,
  inverted,
  onClick,
}: SessionPreviewProps) {
  const outputFiles = extractOutputFiles(events);
  const allPairs = extractMessagePairs(events);
  const pairs = expanded ? allPairs : allPairs.slice(-2);
  const isFailed = agentStatus === "failed";
  const isRunning = agentStatus === "running" || agentStatus === "starting";
  const time = timeAgo(lastActivityAt);
  const fileBandDark = !inverted;

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pairs.length and expanded drive content shape; including them in deps re-runs measure() when the visual layout changes, even though the effect body reads only refs.
  useLayoutEffect(() => {
    const measure = () => {
      const outer = scrollRef.current;
      const inner = contentRef.current;
      if (!outer || !inner) return;
      setOverflows(inner.scrollHeight > outer.clientHeight);
    };
    measure();
    if (!scrollRef.current) return;
    const ro = new ResizeObserver(measure);
    ro.observe(scrollRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [pairs.length, expanded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pairs.length / expanded change content shape; we want the bottom-stick to fire on those even though the effect body only reads scrollRef + overflows.
  useEffect(() => {
    if (scrollRef.current && overflows) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [pairs.length, expanded, overflows]);

  const fadeBg = inverted ? "var(--surface)" : "var(--bg)";

  return (
    <div
      onClick={onClick}
      className="session-card-debug"
      style={{
        display: "flex",
        flex: "1 1 0",
        minWidth: 0,
        scrollSnapAlign: "start",
        cursor: "pointer",
        overflow: "hidden",
        position: "relative",
        background: inverted
          ? isFailed
            ? "rgba(181,64,64,0.06)"
            : "var(--surface)"
          : isFailed
            ? "var(--err-bg)"
            : "transparent",
        borderRadius: inverted ? "6px" : "0",
        border: inverted ? "1px solid rgba(255,255,255,0.08)" : "none",
        borderLeft:
          !inverted && showBorder ? "1px solid var(--blt)" : undefined,
        marginLeft: inverted && showBorder ? "var(--sp-1)" : undefined,
        marginBottom: inverted ? "var(--sp-1)" : undefined,
      }}
    >
      {/* Conversation log — scrolls, bottom-anchored when overflowing */}
      <div
        ref={scrollRef}
        className="session-text-debug hide-scrollbar"
        style={{
          flex: "1 1 0",
          minWidth: 0,
          padding: expanded
            ? "var(--sp-4) var(--sp-4) var(--sp-6)"
            : "var(--sp-2) var(--sp-3) var(--sp-5)",
          display: "flex",
          flexDirection: "column",
          justifyContent: overflows ? "flex-end" : "flex-start",
          overflow: "hidden",
        }}
      >
        <div
          ref={contentRef}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: expanded ? "var(--sp-4)" : "var(--sp-1)",
          }}
        >
          {pairs.length === 0 && (
            <p
              style={{
                ...textBase,
                fontFamily: "var(--font-body)",
                fontSize: "var(--text-sm)",
                color: "var(--t4)",
                fontStyle: "italic",
              }}
            >
              No messages yet
            </p>
          )}

          {pairs.map((pair, i) => {
            const isLast = i === pairs.length - 1;
            const userClamp = expanded ? 3 : isLast ? 2 : 1;
            const agentClamp = expanded ? 6 : isLast ? 5 : 1;
            return (
              <div
                key={`pair-${i}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: expanded ? "2px" : "1px",
                  flexShrink: 0,
                  paddingTop: expanded && i > 0 ? "var(--sp-2)" : undefined,
                  borderTop:
                    expanded && i > 0 ? "1px solid var(--blt)" : undefined,
                }}
              >
                {pair.user && (
                  <p
                    style={{
                      ...textBase,
                      fontFamily: "var(--font-body)",
                      fontSize: expanded ? "var(--text-sm)" : "var(--text-xs)",
                      lineHeight: "var(--lh-snug)",
                      color: "var(--t3)",
                      display: "-webkit-box",
                      WebkitLineClamp: userClamp,
                      WebkitBoxOrient: "vertical" as const,
                      overflow: "hidden",
                    }}
                  >
                    {pair.user}
                  </p>
                )}
                {pair.agent && (
                  <p
                    style={{
                      ...textBase,
                      fontFamily: "var(--font-body)",
                      fontSize: "var(--text-sm)",
                      lineHeight: expanded
                        ? "var(--lh-body)"
                        : "var(--lh-snug)",
                      fontWeight: 600,
                      color: isFailed && isLast ? "var(--err)" : "var(--t1)",
                      display: "-webkit-box",
                      WebkitLineClamp: agentClamp,
                      WebkitBoxOrient: "vertical" as const,
                      overflow: "hidden",
                    }}
                  >
                    {pair.agent}
                  </p>
                )}
              </div>
            );
          })}

          {/* Status badges */}
          {isFailed && (
            <span
              style={{
                fontSize: "var(--text-2xs)",
                color: "var(--err)",
                fontWeight: 600,
                fontFamily: "var(--font-ui)",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                letterSpacing: "0.02em",
                textTransform: "uppercase" as const,
                flexShrink: 0,
              }}
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              Failed
            </span>
          )}
          {isRunning && (
            <span
              style={{
                fontSize: "var(--text-2xs)",
                color: "var(--accent)",
                fontWeight: 600,
                fontFamily: "var(--font-ui)",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                letterSpacing: "0.02em",
                textTransform: "uppercase" as const,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  background: "var(--accent)",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
              Running
            </span>
          )}
        </div>
      </div>

      {/* Top fade — only shown when content overflows */}
      {overflows && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: outputFiles.length > 0 ? "52px" : 0,
            height: "32px",
            background: `linear-gradient(to bottom, ${fadeBg}, transparent)`,
            pointerEvents: "none",
            borderRadius: inverted ? "6px 6px 0 0" : undefined,
          }}
        />
      )}

      {/* Time — pinned bottom-right */}
      {time && (
        <span
          style={{
            position: "absolute",
            bottom: "var(--sp-2)",
            right: outputFiles.length > 0 ? "60px" : "var(--sp-3)",
            fontSize: "var(--text-2xs)",
            color: "var(--t4)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {time}
        </span>
      )}

      {/* Output files column */}
      {outputFiles.length > 0 && (
        <div
          className="session-files-debug"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-1)",
            padding: "var(--sp-2)",
            borderLeft: "1px solid var(--blt)",
            alignItems: "center",
            justifyContent: expanded ? "center" : "flex-end",
            alignSelf: expanded ? "stretch" : "flex-end",
            flexShrink: 0,
            background: fileBandDark ? "var(--card-bg)" : "var(--white)",
            borderRadius: inverted ? "6px" : "4px",
            marginBottom: "var(--sp-5)",
          }}
        >
          {outputFiles.map((f, i) => (
            <OutputFileChip
              key={`f-${f.name}-${i}`}
              name={f.name}
              dark={fileBandDark}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function GhostSession({
  onClick,
  inverted,
}: {
  wide?: boolean;
  onClick?: () => void;
  inverted?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className="ghost-debug"
      style={{
        flexShrink: 0,
        width: "52px",
        minHeight: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderLeft: `1px solid ${inverted ? "var(--card-border)" : "var(--blt)"}`,
        cursor: "pointer",
        color: inverted ? "var(--card-t3)" : "var(--t4)",
        background: "transparent",
        fontSize: "var(--text-xs)",
        fontFamily: "var(--font-ui)",
        scrollSnapAlign: "start",
      }}
    >
      <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
    </div>
  );
}

export function SessionSkeleton() {
  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 0,
        padding: "var(--sp-2) var(--sp-3)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-1)",
      }}
    >
      <div
        className="animate-pulse"
        style={{
          height: "13px",
          width: "85%",
          background: "var(--border)",
          borderRadius: "2px",
        }}
      />
      <div
        className="animate-pulse"
        style={{
          height: "13px",
          width: "100%",
          background: "var(--border)",
          borderRadius: "2px",
          opacity: 0.7,
        }}
      />
      <div
        className="animate-pulse"
        style={{
          height: "13px",
          width: "60%",
          background: "var(--border)",
          borderRadius: "2px",
          opacity: 0.4,
        }}
      />
    </div>
  );
}
